import Loading from '@tencent/dui/lib/components/Loading';
import i18n from '@i18next/i18n';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import ClearanceUtils from '@src/slide/domain/ui-model/utils/clearance-utils';
import { DataType, IPresentationDataModel, ISlideDataModel } from '@tencent/slide-sdk-model';
import { IResourceMapData } from '@tencent/slide-sdk-model/src/data/resource-map-data';
import CommonTips, { CommonTipsType } from '@src/framework/frame/components/common-tips';
import SdkAttachmentElementFilter from '@src/slide/domain/ui-model/element-modeles/property/attachment/utils/sdk-attachment-element-filter';
import { dataValidateAndReport } from '@src/slide/domain/slide-app/utils/data-validate';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import { LocalEditServices } from '@src/slide/domain/slide-app/scenes/local-edit/core/local-edit-services.core';

interface ILocalImageData {
    src: string;
    width?: number;
    height?: number;
}

const INSERT_AT_THE_END = -2;

let confirmTimer;
let load;

const getAppServices = (): IEditorAppServices => AppServiceProvider.getInstance().getAppServices() as
        IEditorAppServices;

const getSelectionService = (): ISelectionService => getAppServices().viewableServiceLayer.selectionService;

const getUiDataService = (): IUiDataService => getAppServices().getUiDataService();

/**
 * 按页导入幻灯片
 * @param importPresentation
 * @param onClose
 * @param useCurrentSlideIndex
 */
export const importSlidesV3 = (importPresentation, onClose, useCurrentSlideIndex) => {
    // 大型 PPT 合并的时候巨卡，加 loading 缓解下
    if (!load) {
        load = Loading.show({ className: 'paging-selection-combine', tip: i18n.t('导入合并中...') });
    }
    clearTimeout(confirmTimer);
    confirmTimer = setTimeout(() => {
        const { slideClearanceIndex } = getSelectionService();
        const currentSlideIndex = getSelectionService()?.currentSlideIndex;
        const presentationModel = getUiDataService().presentation;
        const { sectionList } = presentationModel;
        const importStartIndex = (() => {
            if (useCurrentSlideIndex) {
                if (slideClearanceIndex !== -1) {
                    // 选中的就是间隙
                    return slideClearanceIndex;
                }
                // 选中的不是间隙，而是slide页
                if (sectionList?.length) {
                    // 存在节的情况下
                    return ClearanceUtils.calcClearanceIndexFromSlideIndex(currentSlideIndex ?? 0, sectionList);
                }
                // 不存在节的情况下
                return currentSlideIndex + 1;
            }
            return INSERT_AT_THE_END;
        })();
        if (importPresentation) {
            // 导入数据前校验，校验仅上报
            dataValidateAndReport(importPresentation, 'import_pagination', DataType.Presentation);

            // 将导入数据添加到当前文档
            (presentationModel.sdkData as IPresentationDataModel).api
                .addPresentation(importPresentation, importStartIndex, !!sectionList?.length);
        }
        load.close();
        load = null;
        onClose?.();
    }, 300);
};

export const updatePresentationBySelectedV3 = async (
    originPresentation: IPresentationDataModel,
    importPresentation: IPresentationDataModel,
    selectedSlides: ISlideDataModel[],
    keepScale: boolean,
) => {
    /**
     * 桌面端本地编辑插入页，需更新媒体资源为本地资源 http -> tdocs://
     * 移动端暂无需处理，移动端本地文件也是 https 协议文件，客户端劫持处理
     */
    if (ENV.IS_PC_LOCAL_EDIT) {
        const localResourceMap = await downloadResource2Local((importPresentation as any).store.data.resourceMap);
        importPresentation.api.updateResourceMap(localResourceMap);
    }
    const [filteredSlides, isFiltered] = SdkAttachmentElementFilter.filter(selectedSlides);
    if (isFiltered) {
        CommonTips.getInstance().show({
            message: i18n.t('暂不支持跨文档导入本地文件'),
            commonTipsType: CommonTipsType.INFO,
            block: false,
            autoClose: true,
        });
        importPresentation.api.removeSlides(importPresentation.slideList);
        importPresentation.api.addSlides(filteredSlides);
    } else {
        importPresentation.api.removeSlides(importPresentation.slideList
            .filter(({ id }) => !selectedSlides.find(filteredSlide => filteredSlide.id === id)));
    }
    const remainLayouts = selectedSlides.map(slide => slide.getLayout()!);
    const remainLayoutIds = remainLayouts.map(layout => layout.props.id);
    const needRemovedLayouts = importPresentation.layoutList
        .filter(layout => !remainLayoutIds.includes(layout.props.id));
    importPresentation.api.removeLayouts(needRemovedLayouts);
    const remainMasters = importPresentation.layoutList.map(layout => layout.getMaster()!);
    const needRemovedMasters = importPresentation.masterList
        .filter(master => !remainMasters.includes(master));
    importPresentation.api.removeMasters(needRemovedMasters);
    if (keepScale) {
        // 比例变换
        if (originPresentation) {
            const { cx, cy, type } = originPresentation.props.sldSz;
            const res = importPresentation?.api.resizeSlideSize?.(cx, cy, type);
            if (!res) console.warn('unSupport resizeSlideSize');
        }
    }
};

function hasMedia(data: any) {
    return data && Object.keys(data).length > 0;
}

export async function replaceMediaUrl(remoteUrl: string): Promise<ILocalImageData> {
    try {
        const manager = await LocalEditServices.LocalEditManager.promise();
        const { code, data, message } = await manager.downloadImage(remoteUrl);
        if (code !== 0 || !data) {
            window.log?.error('local-edit replaceMediaUrl fail message: ', message);
        }
        return {
            src: data?.url || remoteUrl,
            width: data?.width,
            height: data?.height,
        };
    } catch (error) {
        window.log?.error('local-edit catch replaceMediaUrl fail message: ', error.toString());
        return {
            src: remoteUrl,
        };
    }
}

export const downloadResource2Local = async (resourceMap: IResourceMapData): Promise<IResourceMapData> => {
    const replaceArr: Promise<void>[] = [];
    Object.keys(resourceMap).forEach((resourceType) => {
        if (hasMedia(resourceMap[resourceType])) {
            Object.values(resourceMap[resourceType]).forEach((item: any) => {
                if (item?.src?.startsWith('http')) {
                    // eslint-disable-next-line no-async-promise-executor
                    replaceArr.push(new Promise(async (resolve) => {
                        const { src, width, height } = await replaceMediaUrl(item.src);
                        item.src = src || item.src;
                        item.width = width || item.width;
                        item.height = height || item.height;
                        resolve();
                    }));
                }
            });
        }
    });
    // 等待所有图片都下载结束
    if (replaceArr.length > 0) {
        await Promise.all(replaceArr);
    }
    return resourceMap;
};
